import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { SEQUENTIAL, INK, FONT, inkOn } from './vizTokens';
import { columnTotals } from '../../utils/heatGrid';

/**
 * Department × stage as a heat grid.
 *
 * This is the view that turns two separate totals into an answer: "Finance has
 * four sitting with legal" is not derivable from a department count and a stage
 * count side by side.
 *
 * Magnitude across a grid is a sequential job — one hue, light→dark — so the
 * reader compares depth, not hue. Cells carry their value as text as well, so
 * nothing is encoded by colour alone.
 */
// `onSelectTotal(stage)`, when given, makes a cell of the totals row clickable.
const HeatGrid = ({ rows, stages, onSelect, onSelectTotal, emptyText = 'Nothing to show' }) => {
  if (!rows || rows.length === 0) {
    return (
      <Typography sx={{ ...FONT, color: INK.muted, fontSize: '13px', py: 2 }}>
        {emptyText}
      </Typography>
    );
  }

  const max = Math.max(1, ...rows.flatMap((r) => r.stages.map((c) => c.count)));

  // Zero is the surface, never the palest ink step — an empty cell should read
  // as absence, not as a small value.
  const stepFor = (count) => {
    if (count === 0) return null;
    const ratio = count / max;
    const i = Math.min(SEQUENTIAL.length - 1, Math.ceil(ratio * (SEQUENTIAL.length - 1)));
    return SEQUENTIAL[i];
  };

  // The last column is each department's total.
  const gridTemplate = `minmax(96px, 150px) repeat(${stages.length}, minmax(48px, 1fr)) minmax(56px, 72px)`;
  const { totals, grandTotal } = columnTotals(rows, stages);
  const rowTotal = (row) => stages.reduce((n, stage) => n + (row.stages.find((c) => c.key === stage.key)?.count ?? 0), 0);

  // Totals are plain bold text, not shaded: a sum is not on the same scale as
  // the cells, and shading it would make it the darkest thing on the grid.
  const totalCell = (count, tooltip, onClick) => (
    <Tooltip title={tooltip} placement="top" arrow>
      <Box
        onClick={onClick}
        sx={{
          height: 34, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? { backgroundColor: INK.plane } : undefined,
        }}
      >
        <Typography
          sx={{
            ...FONT, fontSize: '13px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            color: count > 0 ? INK.primary : INK.muted,
          }}
        >
          {count.toLocaleString()}
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    // minWidth: 0 is load-bearing. A flex/grid child defaults to min-width:auto,
    // so without it the 520px grid below pushes the whole PAGE wider than the
    // viewport instead of scrolling inside this box — every card on the report
    // then gets clipped at the right edge on a phone.
    <Box sx={{ overflowX: 'auto', minWidth: 0, maxWidth: '100%' }}>
      <Box sx={{ minWidth: 520 }}>
        {/* Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: gridTemplate, gap: '2px', mb: '2px' }}>
          <Box />
          {stages.map((stage) => (
            <Typography
              key={stage.key}
              sx={{
                ...FONT,
                fontSize: '11px',
                color: INK.secondary,
                lineHeight: 1.25,
                textAlign: 'center',
                px: 0.25,
              }}
            >
              {stage.label}
            </Typography>
          ))}
          <Typography
            sx={{ ...FONT, fontSize: '11px', fontWeight: 600, color: INK.primary, lineHeight: 1.25, textAlign: 'center' }}
          >
            Total
          </Typography>
        </Box>

        {/* A 2px gap in the surface colour is what separates touching cells —
            no borders, which would add ink that is not data. */}
        {rows.map((row) => (
          <Box
            key={row.department}
            sx={{ display: 'grid', gridTemplateColumns: gridTemplate, gap: '2px', mb: '2px' }}
          >
            <Typography
              sx={{
                ...FONT,
                fontSize: '13px',
                color: row.department === 'Unassigned' ? INK.muted : INK.secondary,
                fontStyle: row.department === 'Unassigned' ? 'italic' : 'normal',
                display: 'flex',
                alignItems: 'center',
                pr: 1,
                lineHeight: 1.2,
              }}
            >
              {row.department}
            </Typography>

            {stages.map((stage) => {
              const count = row.stages.find((c) => c.key === stage.key)?.count ?? 0;
              const fill = stepFor(count);
              const clickable = Boolean(onSelect) && count > 0;

              return (
                <Tooltip
                  key={stage.key}
                  title={`${row.department} · ${stage.label}: ${count.toLocaleString()}`}
                  placement="top"
                  arrow
                >
                  <Box
                    onClick={clickable ? () => onSelect(row.department, stage) : undefined}
                    sx={{
                      height: 34,
                      borderRadius: '4px',
                      backgroundColor: fill || INK.plane,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: clickable ? 'pointer' : 'default',
                      transition: 'transform 120ms ease',
                      '&:hover': clickable ? { transform: 'scale(1.04)' } : undefined,
                    }}
                  >
                    <Typography
                      sx={{
                        ...FONT,
                        fontSize: '13px',
                        fontWeight: count > 0 ? 600 : 400,
                        fontVariantNumeric: 'tabular-nums',
                        // The one place text sits inside a data colour, so it
                        // picks its side from the fill's luminance.
                        color: fill ? inkOn(fill) : INK.muted,
                      }}
                    >
                      {count === 0 ? '·' : count}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
            {totalCell(rowTotal(row), `${row.department} · all statuses: ${rowTotal(row).toLocaleString()}`)}
          </Box>
        ))}

        {/* Totals across the departments, on a rule under the grid. */}
        <Box
          sx={{
            display: 'grid', gridTemplateColumns: gridTemplate, gap: '2px',
            borderTop: `1px solid ${INK.border}`, mt: '6px', pt: '6px',
          }}
        >
          <Typography
            sx={{ ...FONT, fontSize: '13px', fontWeight: 600, color: INK.primary, display: 'flex', alignItems: 'center', pr: 1 }}
          >
            Total
          </Typography>
          {stages.map((stage) => {
            const count = totals.find((t) => t.key === stage.key)?.count ?? 0;
            return (
              <React.Fragment key={stage.key}>
                {totalCell(count, `All departments · ${stage.label}: ${count.toLocaleString()}`,
                  onSelectTotal && count > 0 ? () => onSelectTotal(stage) : undefined)}
              </React.Fragment>
            );
          })}
          {totalCell(grandTotal, `All departments · all statuses: ${grandTotal.toLocaleString()}`)}
        </Box>

        {/* Legend: the scale, since colour here is continuous. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <Typography sx={{ ...FONT, fontSize: '11px', color: INK.muted }}>Fewer</Typography>
          <Box sx={{ display: 'flex', gap: '2px' }}>
            {SEQUENTIAL.map((c) => (
              <Box key={c} sx={{ width: 18, height: 8, borderRadius: '2px', backgroundColor: c }} />
            ))}
          </Box>
          <Typography sx={{ ...FONT, fontSize: '11px', color: INK.muted }}>
            More (max {max.toLocaleString()})
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HeatGrid;
